import FileUploadOutlinedIcon from '@mui/icons-material/FileUploadOutlined';
import FolderOutlinedIcon from '@mui/icons-material/FolderOutlined';
import FolderZipOutlinedIcon from '@mui/icons-material/FolderZipOutlined';
import InsertDriveFileOutlinedIcon from '@mui/icons-material/InsertDriveFileOutlined';
import {Box, List, ListItem, Typography} from '@mui/material';
import {useCallback, useEffect, useMemo, useRef, useState} from 'react';
import {FCLocalDirectory} from '../../../../../types/upload-new-eviidence/fcUploadFileTypes';
import {
  getDateInUTCFormat,
  getFileSize,
  isFileExtensionBlocked,
  isZipfile,
} from '../../../../../utils/fcUploadFileUtils';
import AurButton from '../../../../common/aur-button/AurButton';
import AurIconButton from '../../../../common/aur-button/AurIconButton';
import AurIcon, {
  IconColors,
  IconNames,
} from '../../../../common/aur-icon/AurIcon';
import {AurTextField} from '../../../../common/aur-text-field/AurTextField';

import {t} from 'i18next';
import {Panel, PanelGroup, PanelResizeHandle} from 'react-resizable-panels';
import {isEmptyInstance} from '../../../../../utils/utils';
import AurLoadingIndicator from '../../../../common/aur-loading-indicator/AurLoadingIndictor';
import { useAnalytics } from '../../../../../hooks/useAnalytics';
import { GA_CLICK_TYPES, GA_EVENT_NAMES, GA_REGIONS, GA_SUBPAGE_TYPE } from '../../../../../constants/analyticsConstants';

interface FCLocalFileListProps {
  isActiveTab: boolean;
  currentDirectory: string;
  setCurrentDirectory: (path: string) => void;
  fetchFilesForCurrentDirectory: (path: string) => void;
  directoryFiles: FCLocalDirectory[];
  isLocalFilesLoading: boolean;
  handleUploadLocalFile: (directory: FCLocalDirectory) => void;
}

const FC_RESIZER_PANEL_KNOB_HEIGHT = 16;
const FC_VISIBLE_ROWS_IN_MIN_PANEL_SIZE = 2;
const FC_VISIBLE_ROWS_IN_DEFAULT_PANEL_SIZE = 5;

const FCLocalFileList: React.FC<FCLocalFileListProps> = ({
  isActiveTab,
  currentDirectory,
  setCurrentDirectory,
  fetchFilesForCurrentDirectory,
  directoryFiles,
  isLocalFilesLoading,
  handleUploadLocalFile,
}) => {
    const {trackGAEvent} = useAnalytics();
  
  const filteredDirectoryFiles = useMemo(
    () =>
      directoryFiles
        .filter(
          (file: FCLocalDirectory) =>
            file.name !== '..' &&
            !file?.isHidden &&
            !isFileExtensionBlocked(file)
        )
        .filter(
          (file: FCLocalDirectory) =>
            ((file?.isFile ?? false) && (file?.size ?? 0) > 0) ||
            (file?.isDirectory ?? false)
        ),
    [directoryFiles]
  );

  const listingContainerRef = useRef<HTMLDivElement | null>(null);
  const [listingHeight, setListingHeight] = useState<number>(0);
  const [listingCollapsedHeight, setListingCollapsedHeight] =
    useState<number>(0);

  const listingBottomMargin = useMemo(
    () => -listingHeight + (listingCollapsedHeight * listingHeight) / 100,
    [listingCollapsedHeight, listingHeight]
  );

  useEffect(() => {
    if (!isEmptyInstance(listingContainerRef.current)) {
      const listingOffsetHeight =
        listingContainerRef.current?.offsetHeight ?? 0;
      setListingHeight((prevValue) =>
        listingOffsetHeight === prevValue ? prevValue : listingOffsetHeight
      );
    }
  }, [listingContainerRef, filteredDirectoryFiles, isActiveTab]);

  const minPanelSize = useMemo(
    () =>
      filteredDirectoryFiles.length > FC_VISIBLE_ROWS_IN_MIN_PANEL_SIZE
        ? (FC_VISIBLE_ROWS_IN_MIN_PANEL_SIZE *
            (listingHeight / filteredDirectoryFiles.length) *
            100) /
          listingHeight
        : 100,
    [filteredDirectoryFiles.length, listingHeight]
  );

  const defaultPanelSize = useMemo(() => {
    const visibleRowsHeightInDefaultPanelSize =
      filteredDirectoryFiles.length > 0
        ? (FC_VISIBLE_ROWS_IN_DEFAULT_PANEL_SIZE * listingHeight) /
          filteredDirectoryFiles.length
        : 0;

    return filteredDirectoryFiles.length <=
      FC_VISIBLE_ROWS_IN_DEFAULT_PANEL_SIZE
      ? 100
      : Math.min(
          (visibleRowsHeightInDefaultPanelSize * 100) / listingHeight,
          100
        );
  }, [filteredDirectoryFiles.length, listingHeight]);

  const handleFilePathInputKeyDown = useCallback(
    (event: React.KeyboardEvent<HTMLInputElement>) => {
      const target = event.target as HTMLInputElement;
      if (event.key === 'Enter') {
        fetchFilesForCurrentDirectory(target?.value ?? '');
      }
    },
    [fetchFilesForCurrentDirectory]
  );

  const renderNoFileList = () => {
    return (
      <Box className={'no-local-files-list-container'}>
        <Typography
          tabIndex={0}
          className="typo-text body1_size subtitle-text-color"
        >
          {`${t('fileCatalyst.noFilesAvailable')}`}
        </Typography>
      </Box>
    );
  };

  const renderFileIcon = useCallback((directory: FCLocalDirectory) => {
    if (directory?.isDirectory ?? false) {
      return <FolderOutlinedIcon className="file-icon" />;
    } else if (isZipfile(directory)) {
      return <FolderZipOutlinedIcon className="file-icon" />;
    } else {
      return <InsertDriveFileOutlinedIcon className="file-icon" />;
    }
  }, []);

  const renderFileListing = useCallback(
    () => (
      <div
        key={`${isActiveTab}`}
        className={'local-file-list-container'}
        ref={listingContainerRef}
      >
        <List disablePadding>
          {filteredDirectoryFiles.map((directory: FCLocalDirectory) => {
            return (
              <ListItem
                key={directory?.path}
                disablePadding
                disableGutters
                divider
              >
                <Box className={'local-file-item-container'}>
                  <Box
                    className={'local-file-list-sub-container'}
                    onClick={
                      directory?.isDirectory
                        ? () => fetchFilesForCurrentDirectory(directory?.path)
                        : () => null
                    }
                  >
                    <Box className={'local-file-icons-container'}>
                      <AurButton
                        classNames={'upload-button'}
                        type="text"
                        size="Small"
                        onClick={(e) => {
                          e.stopPropagation();
                                        trackGAEvent({
                                eventName: GA_EVENT_NAMES.CLICK,
                                click: {
                                  name: t('gaTracking.uploadEvidence.upload'),
                                  type: GA_CLICK_TYPES.ICON,
                                  region: GA_REGIONS.TAB,
                                  action: t('gaTracking.uploadEvidence.uploadFileBegin')
                                },
                                
                                subPage: {
                                subpage_name : t('gaTracking.uploadEvidence.highSpeedUpload'),
                                subpage_type : GA_SUBPAGE_TYPE.TAB
                                }
                              });
                          handleUploadLocalFile(directory);
                        }}
                      >
                        <FileUploadOutlinedIcon />
                      </AurButton>
                      {renderFileIcon(directory)}
                    </Box>
                    <Box className={'local-file-desc-container'}>
                      <Typography
                        className="typo-text body3_size text-color directory-name-text"
                        tabIndex={0}
                      >
                        {directory?.name}
                      </Typography>
                      <Typography
                        className="typo-text body3_size file-modified text-color"
                        tabIndex={0}
                      >
                        {getDateInUTCFormat(directory?.lastModified)}
                      </Typography>
                      <Typography
                        className="typo-text body3_size file-size text-color"
                        tabIndex={0}
                      >
                        {getFileSize(directory?.size)}
                      </Typography>
                    </Box>
                  </Box>
                </Box>
              </ListItem>
            );
          })}
        </List>
      </div>
    ),
    [
      fetchFilesForCurrentDirectory,
      filteredDirectoryFiles,
      handleUploadLocalFile,
      isActiveTab,
      renderFileIcon,
    ]
  );

  return (
    <div
      className={'fc-local-file-list-container'}
      style={
        filteredDirectoryFiles.length > 0
          ? {
              marginBottom: listingBottomMargin,
            }
          : {}
      }
    >
      <Typography className="typo-text subhead2_size text-color" tabIndex={0}>
        {t('fileCatalyst.localFileSystem')}
      </Typography>
      <Typography
        className="typo-text body3_size subtitle-text subtitle-text-color"
        tabIndex={0}
      >
        {t('fileCatalyst.localFileSystemDesc')}
      </Typography>
      <Box className={'local-files-master-container'}>
        <Box className={'local-file-input-container'}>
          <AurIconButton
            className="up-button"
            size={'big'}
            icon={
              <AurIcon
                icon={IconNames.LeftUpArrowIcon}
                color={IconColors.Custom}
                customClassName={'up-button-icon'}
              />
            }
            onClick={() =>
              fetchFilesForCurrentDirectory(directoryFiles?.[0]?.path)
            }
          />
          <Typography className="typo-text body2_size text-color">{`${t('fileCatalyst.thisPC')} /`}</Typography>
          <AurTextField
            className="local-file-path-input"
            variant={'outlined'}
            value={currentDirectory}
            autoComplete="off"
            autoCorrect="off"
            spellCheck="false"
            ariaLabel={t('fileCatalyst.thisPC')}
            onChange={(e) => {
              setCurrentDirectory(e.target.value);
            }}
            onKeyDown={handleFilePathInputKeyDown}
          />
          <div className={'vertical-border'} />
          <AurButton
            type={'text'}
            classNames={'enter-path-button'}
            label={t('fileCatalyst.enterPathManually')}
            onClick={() => {
              trackGAEvent({
                                eventName: GA_EVENT_NAMES.CLICK,
                                click: {
                                  name: t('gaTracking.uploadEvidence.enterPathManually'),
                                  type: GA_CLICK_TYPES.BUTTON,
                                  region: GA_REGIONS.TAB,
                                  action: t('gaTracking.uploadEvidence.uploadFileFilePathEntered')
                                },
                                
                                subPage: {
                                subpage_name : t('gaTracking.uploadEvidence.highSpeedUpload'),
                                subpage_type : GA_SUBPAGE_TYPE.TAB
                                }
                              });
              fetchFilesForCurrentDirectory(currentDirectory);
            }}
          />
        </Box>
        {filteredDirectoryFiles.length > 0 ? (
          <div
            className="resizable-panel-group-container"
            style={{
              height: `${listingHeight + FC_RESIZER_PANEL_KNOB_HEIGHT}px`,
            }}
            key={`${filteredDirectoryFiles?.[0]?.path ?? ''}-${listingHeight}`}
          >
            <PanelGroup className={'panel-group'} direction={'vertical'}>
              <Panel
                style={{
                  overflowY: isLocalFilesLoading ? 'hidden' : 'auto',
                }}
                className={'listing-panel'}
                onResize={setListingCollapsedHeight}
                minSize={minPanelSize}
                defaultSize={defaultPanelSize}
              >
                {renderFileListing()}
                {isLocalFilesLoading ? (
                  <Box className={'loading-container'}>
                    <AurLoadingIndicator
                      overlay={false}
                      size={30}
                      loaderThickness={7}
                      show={isLocalFilesLoading}
                    />
                  </Box>
                ) : null}
              </Panel>
              <PanelResizeHandle className={'resize-handler'}>
                <div className={'resize-handler-knob'} />
              </PanelResizeHandle>
              <Panel minSize={0} />
            </PanelGroup>
          </div>
        ) : (
          <div>
            {renderNoFileList()}
            <div className={'resize-handler'}>
              <div className={'resize-handler-knob'} />
            </div>
          </div>
        )}
      </Box>
    </div>
  );
};

export default FCLocalFileList;
